/** @jsxImportSource @emotion/react */

import React, { memo, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  TextField,
  Typography,
  InputAdornment,
  Checkbox,
  Radio,
  Box,
} from "@material-ui/core";
import { xorBy, unionBy } from "lodash";

import { Button, Icon, Table, Pagination, ViewState } from "..";
import { Props as PaginationProps } from "../Pagination";
import { Props as TableProps } from "../Table";
import { State as ViewStateType } from "../ViewState";

import {
  dialog,
  section,
  dialogTitle,
  dialogActions,
  btn,
  dialogContentBlock,
  contentHead,
  labelText,
  refreshButton,
  refreshIcon,
  searchBox,
  searchIcon,
  emptySection,
  emptyBox,
} from "./styles";

export interface Props {
  open: boolean;
  title: string;
  label: string;
  selectedRows: Array<any>;
  tableProps: TableProps & {
    selectionType: "radio" | "checkbox";
  };
  paginationProps: PaginationProps;
  noDataRender?: React.ReactNode;
  viewStatus: ViewStateType;
  onReload: () => void;
  onClose: () => void;
  onSelect: (records: any[]) => void | Promise<void>;
  onSearch?: (searchText: string) => void;
}

const SelectorModal = ({
  open,
  title,
  label,
  selectedRows,
  tableProps,
  paginationProps,
  noDataRender,
  viewStatus,
  onReload,
  onClose,
  onSelect,
  onSearch,
}: Props): JSX.Element => {
  const intl = useIntl();
  const [selectedRecords, setSelectedRecords] = useState<any[]>([]);
  const { watch, errors } = useForm<{
    searchText: string;
  }>();
  const searchText = watch("searchText", "");
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setSelectedRecords(selectedRows);
  }, [selectedRows]);

  const selectedRecordsSet = new Set(selectedRecords.map(({ id }) => id));
  const { selectionType, bodyRows, ...othersTableProps } = tableProps;
  const isAllToggle = bodyRows.every((item) => selectedRecordsSet.has(item.id));

  return (
    <Dialog fullWidth maxWidth="md" open={open} css={dialog} disableEnforceFocus>
      <DialogTitle css={section} disableTypography>
        <div css={dialogTitle}>{title}</div>
        <div css={dialogActions}>
          <Button color="white" css={btn} onClick={onClose} disabled={loading}>
            {intl.formatMessage({ id: "btn.cancel" })}
          </Button>
          <Button
            variant="contained"
            color="primary"
            css={btn}
            disabled={!selectedRecords.length || loading}
            onClick={async () => {
              setLoading(true);
              await onSelect(selectedRecords);
              setLoading(false);
            }}
            loading={loading}
            icon="check"
          >
            {intl.formatMessage({ id: "btn.select" })}
          </Button>
        </div>
      </DialogTitle>
      <DialogContent dividers css={dialogContentBlock}>
        <Grid container spacing={3} style={{ height: "100%" }}>
          <Grid item xs={12} css={section}>
            <div css={contentHead}>
              <Typography display="inline" css={labelText}>
                {label}
              </Typography>
              <Button color="primary" onClick={onReload} css={refreshButton} disabled={loading}>
                <Icon name="refresh" viewBox="0 0 48 48" css={refreshIcon} />
                {intl.formatMessage({ id: "btn.refresh" })}
              </Button>
            </div>
            {onSearch && (
              <TextField
                css={searchBox}
                fullWidth
                name="searchText"
                placeholder={intl.formatMessage({
                  id: "vod-to-live-page.file.search",
                })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon name="search" viewBox="0 0 48 48" css={searchIcon} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Icon name="close" viewBox="0 0 48 48" />
                    </InputAdornment>
                  ),
                }}
                error={!!errors.searchText}
                defaultValue={searchText}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    onSearch(searchText);
                  }
                }}
              />
            )}
          </Grid>
          <Grid item xs={12} css={emptySection}>
            <ViewState state={viewStatus}>
              {!tableProps.bodyRows.length ? (
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" css={emptyBox}>
                  {noDataRender}
                </Box>
              ) : (
                <>
                  <Table
                    selectionCell={{
                      selectionInHead: selectionType === "checkbox",
                      cellWidth: "5%",
                      renderCell: (rowData) =>
                        selectionType === "radio" ? (
                          <Radio
                            name={`radio-${rowData.id}`}
                            value={rowData.id}
                            color="primary"
                            checked={selectedRecordsSet.has(rowData.id)}
                            onClick={() => setSelectedRecords([rowData])}
                          />
                        ) : (
                          <Checkbox
                            name={`checkbox-${rowData.id}`}
                            value={rowData.id}
                            color="primary"
                            checked={isAllToggle || selectedRecordsSet.has(rowData.id)}
                            onClick={() => {
                              if (!Object.keys(rowData)?.length) {
                                setSelectedRecords(
                                  isAllToggle
                                    ? xorBy(bodyRows, selectedRecords, "id")
                                    : unionBy(bodyRows, selectedRecords, "id")
                                );
                              } else if (selectionType === "checkbox") {
                                setSelectedRecords(xorBy([rowData], selectedRows, "id"));
                              }
                            }}
                          />
                        ),
                    }}
                    bodyRows={bodyRows}
                    {...othersTableProps}
                  />
                  <Pagination {...paginationProps} disabled={loading} />
                </>
              )}
            </ViewState>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default memo(SelectorModal);
